import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import modalStore from 'shared/ui/Modal/store/modalStore'
import type { AxiosOptions } from 'shared/api'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { ConversationsApi } from 'entities/Conversation/api/conversations'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { conversationTemplate } from 'entities/Conversation/templates/conversationTemplate'
import {
  type IParamsConversationCreate,
  type IResponseConversation,
  type IResponseConversationDraftMessage,
} from 'entities/Conversation/api/types'
import type { IUpdateItemOptions } from 'entities/Conversation/store/types'
import type { IResponseUser } from 'entities/Users/api/types'
import { ConversationDraftMessage } from 'entities/Conversation/model/ConversationDraftMessage'
import type { Contact } from 'entities/Contacts/model/Contact'
import { Conversation } from 'entities/Conversation/model'
import {
  type EntitiesStore,
  type IEntitiesStore,
} from 'entities/EntitiesExport/store/entitiesExportStore'
import { type InboxesStore } from 'entities/Inbox'
import { type UsersStore } from 'entities/Users'
import { type ContactsStore } from 'entities/Contacts'
import { type NumbersStore } from 'entities/Phone'
import { ConversationGroupError } from 'entities/Conversation/model/ConversationGroupError'
import { ConversationOpenStore } from './conversationOpen'

export class ConversationsStore {
  private _itemsMap: Map<number, Conversation> = new Map()
  private _currentItemId: number | null = null
  private _prevItemId: number | null = null
  private _nextItemId: number | null = null
  private _itemsDraftMap: Map<number, ConversationDraftMessage> = new Map()
  private readonly _conversationOpenStore: ConversationOpenStore
  private _modeNotOpen = false
  private _inboxesStore: InboxesStore | null = null
  private _usersStore: UsersStore | null = null
  private _contactsStore: ContactsStore | null = null
  private _numbersStore: NumbersStore | null = null
  private _itemsTotalCount: number | null = null

  constructor(private _entities: EntitiesStore) {
    this._conversationOpenStore = new ConversationOpenStore(this)

    makeAutoObservable(this)

    this.setEntities(this._entities)
  }

  get inboxesStore() {
    return this._inboxesStore
  }

  get usersStore() {
    return this._usersStore
  }

  get contactsStore() {
    return this._contactsStore
  }

  get numbersStore() {
    return this._numbersStore
  }

  get conversationOpenStore() {
    return this._conversationOpenStore
  }

  get currentItem() {
    if (this._currentItemId === null) return

    return this.getItem(this._currentItemId)
  }

  get isNewConversation() {
    return this._currentItemId === 0
  }

  get itemsList() {
    return Array.from(this._itemsMap.values())
  }

  get itemsCount() {
    return this._itemsMap.size
  }

  get itemsDraftCount() {
    return this._itemsDraftMap.size
  }

  get itemsLimitTrialCount() {
    const items = this.itemsList.filter((item) => item.recent_message !== null)
    return items.length
  }

  get isEmpty() {
    return this._itemsMap.size === 0
  }

  get currentItemId() {
    return this._currentItemId
  }

  get prevItemId() {
    return this._prevItemId
  }

  get nextItemId() {
    return this._nextItemId
  }

  get modeNotOpen() {
    return this._modeNotOpen
  }

  get signal() {
    return this._conversationOpenStore.signal
  }

  clearSignal = () => {
    this._conversationOpenStore.clearSignal()
  }

  setCurrentItemId = (id: number | null) => {
    if (id) this._prevItemId = id

    this._currentItemId = id
  }

  setModeNotOpen = (status: boolean) => {
    this._modeNotOpen = status
  }

  setNextItemId = (id: number | null) => {
    this._nextItemId = id
  }

  private _updateContacts = (item: IResponseConversation) => {
    const participants = Array.isArray(item.participants) ? item.participants : []

    this.contactsStore?.addItems([...(item.contact ? [item.contact] : []), ...participants])
  }

  addItem = (item: IResponseConversation) => {
    const conversation = new Conversation(item, this._entities)

    this._itemsMap.set(item.id, conversation)

    this._updateContacts(item)

    if (item.owner?.id) {
      this.usersStore?.addItem(item.owner)
    }

    if (item?.recent_message?.user?.id) {
      this.usersStore?.addItem(item.recent_message.user)
    }

    if (item.number) {
      this.numbersStore?.addItem(item.number)
    }

    if (item.draft_message) {
      this.addItemDraft(item.draft_message)
    }

    return conversation
  }

  addItems = (conversations: IResponseConversation[]) => {
    conversations.forEach((item) => {
      this.addItem(item)
    })
  }

  getItem = (id?: number) => {
    if (typeof id !== 'number') return

    return this._itemsMap.get(id)
  }

  deleteItem = (id: number) => {
    this._itemsMap.delete(id)
  }

  hasItem = (id: number) => {
    return this._itemsMap.has(id)
  }

  getItemDraft = (id: number) => {
    return this._itemsDraftMap.get(id)
  }

  hasItemDraft = (id: number) => {
    return this._itemsDraftMap.has(id)
  }

  addItemDraft = (item?: IResponseConversationDraftMessage | null) => {
    if (!item) return

    this._itemsDraftMap.set(item.conversation_id, new ConversationDraftMessage(item))
  }

  increaseItemsTotalDraftCount = (id: number) => {
    if (
      this.inboxesStore?.localInboxes.hasFilterDraft &&
      (!this.hasItemDraft(id) || (this.itemsDraftCount === 1 && this._itemsTotalCount === 0))
    ) {
      this.increaseItemsTotalCount()
    }
  }

  deleteItemDraft = (id: number) => {
    if (this.inboxesStore?.localInboxes.hasFilterDraft && this.hasItemDraft(id)) {
      this.decreaseItemsTotalCount()
    }
    this._itemsDraftMap.delete(id)
  }

  cancelDebounceOnCreateDraft = () => {
    if (this._currentItemId) {
      const conversation = this.getItem(this._currentItemId)

      conversation?.cancelDebounceOnCreateDraft()
    }
  }

  updateItem = (data: IResponseConversation, options?: IUpdateItemOptions) => {
    const conversation = this.getItem(data.id)

    if (conversation) {
      const unreadMentionsCount = options?.updateMentionsCount
        ? data.unread_mentions_count
        : conversation.unread_mentions_count

      conversation.syncOrigin({
        ...conversation.origin,
        ...data,
        unread_mentions_count: unreadMentionsCount,
      })

      if (data.owner?.id) this.usersStore?.addItem(data.owner)
      this._updateContacts(data)
    } else {
      this.addItem(data)
    }
  }

  setItemsTotalCount = (count: number) => {
    if (this.inboxesStore?.localInboxes.hasQuickFilter) this._itemsTotalCount = count
  }

  increaseItemsTotalCount = () => {
    if (!this._itemsTotalCount) {
      this._itemsTotalCount = 1

      return
    }

    this._itemsTotalCount += 1
  }

  decreaseItemsTotalCount = () => {
    if (!!this._itemsTotalCount) this._itemsTotalCount -= 1
  }

  resetItemsTotalCount = () => {
    this._itemsTotalCount = null
  }

  resetCurrentItem = () => {
    if (!this.isNewConversation) {
      this._currentItemId = null
    }
  }

  reset = () => {
    this._itemsMap.clear()
    this._itemsDraftMap.clear()
    this._nextItemId = null
    this.resetItemsTotalCount()
  }

  onRead = async (id: number) => {
    try {
      const conversation = this.getItem(id)

      if (
        !conversation?.isUnread &&
        !conversation?.isUnreadManual &&
        !conversation?.isUnreadMention
      )
        return

      const { data } = await ConversationsApi.updateByIdRead(id)
      this.addItems([data])
    } catch (e) {
      logger.error(e)
    }
  }

  onUnread = async (id: number) => {
    try {
      const { data } = await ConversationsApi.updateByIdUnread(id)
      this.addItems([data])
    } catch (e) {
      logger.error(e)
    }
  }

  getById = async ({
    id,
    update = false,
    isModalError = false,
    isFetch = false,
    options,
  }: {
    id: number
    update?: boolean
    isModalError?: boolean
    isFetch?: boolean
    options?: AxiosOptions
  }) => {
    try {
      let conversation

      if (!isFetch) conversation = this.getItem(id)

      if (!conversation) {
        const { data } = await ConversationsApi.getById(id, options)

        conversation = new Conversation(data, this._entities)

        if (update) {
          this.updateItem(data)
        }

        const users: IResponseUser[] = []
        const contacts = [...(data.contact ? [data.contact] : []), ...data.participants]

        if (data?.owner?.id) {
          users.push(data.owner)
        }

        if (data?.recent_message?.user?.id) {
          users.push(data.recent_message.user)
        }

        this.usersStore?.addItems(users)
        this.contactsStore?.addItems(contacts)
      }

      return conversation
    } catch (e) {
      logger.error(e)

      if (
        e instanceof AxiosError &&
        e.errors.getError('message') === 'Unauthorized action.' &&
        isModalError
      ) {
        modalStore.addModal({
          disabledOnAllClose: true,
          type: ModalTypeList.INFO,
          title: 'Content unavailable',
          id: nanoid(),
          desc: "We're sorry, but you don't currently have access to this content or it was deleted. Please contact the user who shared the link with you to request access.",
        })
      }
    }
  }

  createConversation = async ({
    contact_id,
    participants,
    team_id,
    number_id,
    smart_option,
  }: IParamsConversationCreate): Promise<Conversation | ConversationGroupError | undefined> => {
    try {
      const params = {
        contact_id: contact_id,
        participants: participants,
        team_id: team_id,
        number_id: number_id,
        smart_option: smart_option,
      }

      const { data } = await ConversationsApi.create(params)

      this.updateItem(data)

      return this.getItem(data.id)
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        const errorMessage = e.errors.getError('error')

        if (errorMessage) {
          toastStore.add({
            title: errorMessage,
            type: 'error',
          })
        }

        const status = e.response?.status
        const data = e.response?.data
        if (status === 422 && data && 'participant_details' in data) {
          return new ConversationGroupError(data, this._entities)
        }
      }
    }
  }

  createNewConversation = (contact?: Contact) => {
    const newConversation: IResponseConversation = {
      ...conversationTemplate,
      id: 0,
      contact_id: contact?.id ?? 0,
      inbox_id: this.inboxesStore?.currentInbox?.id ?? 0,
      user_id: this.usersStore?.user_owner_id ?? 0,
      closed_at: '',
      is_group: false,
      is_locked: true,
      last_message_at: '',
      last_inbound_at: '',
      last_read_at: '',
      group_name: null,
      participants: [],
      number_id: 0,
    }
    const newContact = contact?.origin ?? this.contactsStore?.createEmptyContact()

    this.addItem(newConversation)
    if (newContact) {
      this.contactsStore?.updateItem?.(newContact)
    }
  }

  changePriority = async (id: number, priority: boolean) => {
    try {
      await ConversationsApi.updatePriority(id, priority)
      const conversation = this.getItem(id)
      if (conversation) {
        conversation.updatePriority(priority)
      }
    } catch (e) {
      logger.error(e)
    }
  }

  reassign = async (id: number, user_id: number | null) => {
    try {
      const { data } = await ConversationsApi.updateByIdReassign(id, user_id)
      this.updateItem(data)

      return data
    } catch (e) {
      logger.error(e)
      return null
    }
  }

  reassignBulk = async (
    conversations_ids: number[],
    members_ids: number[],
    team_id: number,
    reassign_all: boolean,
    conversation_filter?: string
  ) => {
    try {
      const { data } = await ConversationsApi.updateByIdReassignBulk({
        conversations_ids,
        members_ids,
        team_id,
        reassign_all,
        conversation_filter,
      })

      return data
    } catch (e) {
      logger.error(e)
      return null
    }
  }

  setEntities = (entities: IEntitiesStore) => {
    this._inboxesStore = entities.inboxesStore
    this._contactsStore = entities.contactsStore
    this._usersStore = entities.usersStore
    this._numbersStore = entities.numbersStore
  }

  get conversationInbox() {
    const inbox = this.inboxesStore?.getItem(this.currentItem?.inbox_id)

    if (inbox?.id === 0) return this.inboxesStore?.sortedFirstInbox

    return this.inboxesStore?.currentInbox || inbox
  }

  get conversationInboxNumber() {
    const inbox =
      (this.conversationInbox instanceof Inbox && this.conversationInbox) ||
      (this.inboxesStore?.currentInbox instanceof Inbox && this.inboxesStore?.currentInbox)

    const numberFromCurrentConversation = this.numbersStore?.getItem(this.currentItem?.number_id)
    const numberFromInbox =
      inbox instanceof Inbox ? this.numbersStore?.getItem(inbox.numberId) : undefined

    return numberFromCurrentConversation || numberFromInbox
  }

  get isActionDisabled() {
    const allNumberIds: number[] = []

    this.inboxesStore?.personalInboxes.forEach((inbox) => {
      if (inbox instanceof Inbox) {
        allNumberIds.push(...inbox.numberIds)
      }
    })

    return allNumberIds.every((numberId) => {
      const number = this.numbersStore?.getItem(numberId)
      return Boolean(number?.isTollFree && !number?.isStatusVerified && !number?.is_aircall)
    })
  }

  get itemsTotalCount() {
    return this._itemsTotalCount ?? 0
  }
}
